/**
 * Wizard step 1 — what to generate: kind pills, campaign (locked from the
 * drawer, pickable from the library), then the data-shape row (windows,
 * layers, generation ceilings) and the objective.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import {
  ListChecks,
  Package,
  RefreshCw,
  Sigma,
  SlidersHorizontal,
} from 'lucide-react';

import { Button, SearchableSelect, SegmentedPills } from '@/components/ui';
import { useOptimizerConfigs } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import {
  findLibraryTarget,
  libraryTargetKey,
  libraryTargets,
} from '@/features/flow-optimizer-library/api/library-targets';

import TeamContextEditor from '../TeamContextEditor';
import type { AiWizardState } from '../use-ai-wizard';
import SetupControlsRow from './SetupControlsRow';

const SetupStep: FC<{ wizard: AiWizardState; campaignLocked: boolean }> = ({
  wizard,
  campaignLocked,
}) => {
  const { options, patch } = wizard;
  const targets = libraryTargets(useOptimizerConfigs().rows);
  const campaignOptions = targets.map((t) => ({
    value: libraryTargetKey(t),
    label: `${t.campaignName} (${t.provider})`,
  }));

  return (
    <div className="space-y-3">
      <SegmentedPills
        label="Generate"
        options={[
          { value: 'formulas', label: 'Formulas only', icon: Sigma },
          { value: 'rules', label: 'Rules only', icon: ListChecks },
          { value: 'package', label: 'Full package', icon: Package },
        ]}
        value={options.target}
        onChange={(target) => patch({ target })}
      />
      {options.target !== 'formulas' ? (
        <div className="space-y-1">
          <SegmentedPills
            label="Rules behavior"
            options={[
              { value: 'rotate', label: 'Rotate & replace', icon: RefreshCw },
              {
                value: 'in-place',
                label: 'Adjust in place',
                icon: SlidersHorizontal,
              },
            ]}
            value={options.ruleStyle}
            onChange={(ruleStyle) => patch({ ruleStyle })}
          />
          <p className="text-[11px] text-muted-foreground">
            {options.ruleStyle === 'rotate'
              ? 'Losers swap out and the queue fills every freed slot.'
              : 'Nothing leaves the pool — rules only brake and re-weight in place.'}
          </p>
        </div>
      ) : null}
      {!campaignLocked ? (
        <label className="block space-y-1 text-xs font-medium">
          Campaign (stats source)
          <SearchableSelect
            options={campaignOptions}
            value={libraryTargetKey(options)}
            onChange={(key) => {
              const picked = findLibraryTarget(targets, String(key));
              if (picked) patch({ provider: picked.provider, campaignId: picked.campaignId });
            }}
            placeholder="Pick a campaign…"
            className="mt-1"
          />
        </label>
      ) : null}
      <SetupControlsRow wizard={wizard} />
      <label className="block space-y-1 text-xs font-medium">
        Objective <span className="font-normal text-muted-foreground">(optional)</span>
        <textarea
          value={options.objective}
          onChange={(e) => patch({ objective: e.target.value })}
          placeholder="e.g. Protect ROI on paths, rotate landers aggressively, kill anything below 50 clicks with no conversions."
          rows={2}
          className="mt-1 w-full rounded-md border border-border bg-background p-2 text-xs"
        />
      </label>
      <TeamContextEditor />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={wizard.buildPrompt}
          disabled={
            wizard.preview.isPending ||
            !options.campaignId ||
            options.dateRanges.length === 0 ||
            (options.dateRanges.includes('custom') &&
              (!options.from || !options.to))
          }
        >
          {wizard.preview.isPending ? 'Building…' : 'Build prompt'}
        </Button>
      </div>
    </div>
  );
};

export default SetupStep;
